import Controller from '@ember/controller';
import { reads } from '@ember/object/computed';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { dateToken } from '@qonto/ui-kit/utils/date-token';
import window from 'ember-window-mock';

import { companyCreationJsURL, jsURL, registerJsURL } from 'register-partners-js/constants/hosts';
import { ContentWrapper } from 'register-partners-js/react/components/content-wrapper';
import { MainLayout } from 'register-partners-js/react/components/layouts/main';

const ONGOING_REGISTRATIONS_SELECTOR_ICONS = {
  NEW_BUSINESS: 'ongoing-registration-new-business',
  EXISTING_BUSINESS: 'ongoing-registration-existing-business',
};

export default class RegistrationsController extends Controller {
  mainLayout = MainLayout;

  @service currentUser;
  @service deviceManager;
  @service intl;
  @service router;
  @service segment;

  @reads('currentUser.user') user;

  @tracked clickedOrganization = null;

  contentWrapper = ContentWrapper;

  get userFirstName() {
    return this.user.firstName ?? this.user.email;
  }

  get ongoingRegistrations() {
    return [
      ...this.ongoingPartnerRegistrations,
      ...this.ongoingRegisterPartnerRegistrations,
      ...this.ongoingRegisterRegistrations,
      ...this.ongoingCapitalDepositRegistrations,
      ...this.ongoingCompanyCreationFlow,
    ];
  }

  get ongoingPartnerRegistrations() {
    return this.partnerRegistrationsWithoutLastOne.map(organization => {
      let { registration } = organization;

      let isCompanyCreation = registration.legalFlow === 'company_creation';
      let date = dateToken({ date: organization.createdAt, locale: this.locale });
      let onClick = () => this.resumeRegistration(organization);

      return {
        title: isCompanyCreation
          ? this.intl.t('resume-screen.company-creation-partner-selector.title', {
              companyName: organization.legalName,
              partnerName: registration.partnerName,
            })
          : this.intl.t('resume-screen.existing-business-selector.title', {
              companyName: organization.legalName,
            }),
        subtitle: isCompanyCreation
          ? this.intl.t('resume-screen.company-creation-partner-selector.subtitle', {
              date,
            })
          : this.intl.t('resume-screen.existing-business-selector.subtitle', {
              date,
            }),
        icon: isCompanyCreation
          ? ONGOING_REGISTRATIONS_SELECTOR_ICONS.NEW_BUSINESS
          : ONGOING_REGISTRATIONS_SELECTOR_ICONS.EXISTING_BUSINESS,
        onClick,
        pending: this.clickedOrganization === organization.id,
      };
    });
  }

  get ongoingRegisterPartnerRegistrations() {
    return this.registerPartnerRegistrationsWithoutLastOne.map(organization => {
      let { partnerRegistration } = organization;
      let isCompanyCreation = partnerRegistration.get('legalFlow') === 'company_creation';
      let date = dateToken({ date: organization.createdAt, locale: this.locale });
      let onClick = () =>
        this.resumeRegisterPartnerRegistration(organization, partnerRegistration.get('id'));

      return {
        title: isCompanyCreation
          ? this.intl.t('resume-screen.company-creation-partner-selector.title', {
              companyName: organization.legalName,
              partnerName: partnerRegistration.partnerName,
            })
          : this.intl.t('resume-screen.existing-business-selector.title', {
              companyName: organization.legalName,
            }),
        subtitle: isCompanyCreation
          ? this.intl.t('resume-screen.company-creation-partner-selector.subtitle', {
              date,
            })
          : this.intl.t('resume-screen.existing-business-selector.subtitle', {
              date,
            }),
        icon: isCompanyCreation
          ? ONGOING_REGISTRATIONS_SELECTOR_ICONS.NEW_BUSINESS
          : ONGOING_REGISTRATIONS_SELECTOR_ICONS.EXISTING_BUSINESS,
        onClick,
        pending: this.clickedOrganization === organization.id,
      };
    });
  }

  get partnerRegistrationsWithoutLastOne() {
    let { pendingPartnerRegistrations, currentRegistrationID } = this.model;
    if (currentRegistrationID) {
      return pendingPartnerRegistrations;
    }
    return pendingPartnerRegistrations.slice(0, pendingPartnerRegistrations.length - 1);
  }

  get registerPartnerRegistrationsWithoutLastOne() {
    let { partnerRegistrationsViaRegister, currentRegistrationID } = this.model;
    if (currentRegistrationID) {
      return partnerRegistrationsViaRegister;
    }

    return partnerRegistrationsViaRegister.slice(0, partnerRegistrationsViaRegister.length - 1);
  }

  get ongoingRegisterRegistrations() {
    return this.model.registerOrganizationsWithPayment.map(organization => {
      let isExistingBusiness = organization.underRegistration === false;
      let date = dateToken({ date: organization.createdAt, locale: this.locale });
      let onClick = () => this.resumeRegisterRegistration(organization);

      return {
        title: isExistingBusiness
          ? this.intl.t('resume-screen.existing-business-selector.title', {
              companyName: organization.name,
            })
          : this.intl.t('resume-screen.company-creation-selector.title', {
              companyName: organization.name,
            }),
        subtitle: isExistingBusiness
          ? this.intl.t('resume-screen.existing-business-selector.subtitle', {
              date,
            })
          : this.intl.t('resume-screen.company-creation-selector.subtitle', {
              date,
            }),
        icon: isExistingBusiness
          ? ONGOING_REGISTRATIONS_SELECTOR_ICONS.EXISTING_BUSINESS
          : ONGOING_REGISTRATIONS_SELECTOR_ICONS.NEW_BUSINESS,
        onClick,
        pending: this.clickedOrganization === organization.id,
      };
    });
  }

  get ongoingCompanyCreationFlow() {
    return this.model.ccOrganizations.map(organization => {
      let date = dateToken({ date: organization.attributes.created_at, locale: this.locale });
      let onClick = () => {
        window.location.href = `${companyCreationJsURL}?organization=${organization.id}`;
      };

      return {
        title: this.intl.t('resume-screen.company-creation-selector.title', {
          companyName: organization.attributes.name,
        }),
        subtitle: this.intl.t('resume-screen.company-creation-selector.subtitle', {
          date,
        }),
        icon: ONGOING_REGISTRATIONS_SELECTOR_ICONS.NEW_BUSINESS,
        onClick,
        pending: this.clickedOrganization === organization.id,
      };
    });
  }

  get ongoingCapitalDepositRegistrations() {
    return this.activeAppOrganizationsInCapitalDepositState.map(organization => {
      let date = dateToken({ date: organization.createdAt, locale: this.locale });
      let onClick = () => this.redirectToApp(organization);

      return {
        title: this.intl.t('resume-screen.company-creation-selector.title', {
          companyName: organization.name,
        }),
        subtitle: this.intl.t('resume-screen.company-creation-partner-selector.subtitle-kdeposit', {
          date,
        }),
        icon: ONGOING_REGISTRATIONS_SELECTOR_ICONS.NEW_BUSINESS,
        isOngoingCapitalDeposit: true,
        onClick,
        pending: this.clickedOrganization === organization.id,
      };
    });
  }

  get currentRegistrationID() {
    let { pendingPartnerRegistrations, partnerRegistrationsViaRegister, currentRegistrationID } =
      this.model;

    let lastOngoingPartnerRegistrationID = pendingPartnerRegistrations.length
      ? pendingPartnerRegistrations.at(-1)?.registration?.id
      : partnerRegistrationsViaRegister.at(-1)?.partnerRegistration?.id;

    return currentRegistrationID ?? lastOngoingPartnerRegistrationID;
  }

  get activeAppOrganizationsInCapitalDepositState() {
    return this.model.activeAppOrganizations.filter(this.isOngoingCapitalDeposit);
  }

  get locale() {
    return this.intl.primaryLocale;
  }

  isOngoingCapitalDeposit(organization) {
    return organization.underRegistration;
  }

  resumeRegistration(organization) {
    this.clickedOrganization = organization.id;
    this.segment.track('onboarding_partner_register_accounts_launcher_resume', {
      is_mobile_browser: this.deviceManager.isMobile,
      is_webview: this.deviceManager.isWebview,
    });

    this.router.replaceWith('registration', organization.registration.id);
  }

  resumeRegisterPartnerRegistration(organization, partnerRegistrationId) {
    this.clickedOrganization = organization.id;
    this.segment.track('onboarding_partner_register_accounts_launcher_resume', {
      is_mobile_browser: this.deviceManager.isMobile,
      is_webview: this.deviceManager.isWebview,
    });

    this.router.replaceWith('registration', partnerRegistrationId);
  }

  resumeRegisterRegistration(organization) {
    this.clickedOrganization = organization.id;
    this.segment.track('onboarding_partner_register_accounts_launcher_resume', {
      is_mobile_browser: this.deviceManager.isMobile,
      is_webview: this.deviceManager.isWebview,
    });

    window.location.href = `${registerJsURL}/organizations/${organization.id}/deposit-capital/payment-success`;
  }

  redirectToApp(organization) {
    let { id, slug, underRegistration } = organization;
    this.clickedOrganization = id;

    this.segment.track('onboarding_accounts_launcher_select', {
      is_mobile_browser: this.deviceManager.isMobile,
      is_webview: this.deviceManager.isWebview,
      organization_id: id,
      under_registration: underRegistration,
    });

    if (this.deviceManager.isMobile || this.deviceManager.isWebview) {
      return this.router.transitionTo('registrations.already-created', id);
    }

    window.location.replace(`${jsURL}/organizations/${slug}`);
  }
}
